import { Link, useSearchParams } from "react-router-dom";
import { API, money } from "@/lib/api";
import { useCheckoutStatus } from "@/hooks/useCheckoutStatus";
import { CheckCircle2, Loader2, XCircle, Heart, Download, PenLine, Gift } from "lucide-react";

export default function CheckoutReturn() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { state, amount, downloads, giftReceipt } = useCheckoutStatus(sessionId);
  const hasPhysical = downloads.length === 0 || downloads.some((d) => !d.online_token);

  return (
    <div className="max-w-xl mx-auto px-5 py-32 text-center">
      {state === "checking" && (<><Loader2 className="h-12 w-12 text-[#8E9A86] mx-auto animate-spin" /><h1 className="font-heading text-4xl text-[#3A3632] mt-6">Confirming your gift…</h1><p className="text-[#6B655F] mt-3">One moment while we wrap everything up.</p></>)}
      {state === "paid" && (
        <div data-testid="checkout-success">
          <CheckCircle2 className="h-14 w-14 text-[#8E9A86] mx-auto" />
          <h1 className="font-heading text-5xl text-[#3A3632] mt-6">Thank you 💛</h1>
          <p className="font-hand text-3xl text-[#C4A4A4] mt-3">We are so grateful you are here.</p>
          {amount > 0 && <p className="text-[#6B655F] mt-4">Your order of <span className="font-medium text-[#3A3632]">{money(amount)}</span> is confirmed.</p>}
          {downloads.length > 0 && (
            <div className="mt-8 bg-[#F2EFEB] border border-[#D9CBA0]/50 rounded-xl p-6 text-left" data-testid="download-section">
              <h2 className="font-heading text-2xl text-[#3A3632] mb-3">Your downloads</h2>
              <div className="space-y-3">
                {downloads.map((d) => (
                  <div key={d.token} className="flex flex-col gap-1">
                    <a href={`${API}/download/${d.token}`} data-testid={`download-link-${d.token}`}
                      className="flex items-center gap-2 text-[#8E9A86] hover:text-[#7A8772] font-medium">
                      <Download className="h-4 w-4" /> Download &amp; print: {d.name}
                    </a>
                    {d.online_token && (
                      <Link to={`/journal/${d.online_token}`} data-testid={`journal-online-${d.online_token}`}
                        className="flex items-center gap-2 text-[#B09090] hover:text-[#9A7A7A] font-medium ml-6 text-sm">
                        <PenLine className="h-4 w-4" /> Or fill it in online &amp; save your entries
                      </Link>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-[#8A8175] mt-3">We've also emailed these links to you. Save them somewhere safe — your online journal remembers your entries and opens on any device.</p>
            </div>
          )}
          {hasPhysical && <p className="text-[#6B655F] mt-4 flex items-center justify-center gap-2"><Heart className="h-4 w-4 text-[#C4A4A4]" /> Your Hope Card is being prepared, sent with care.</p>}
          {giftReceipt && (
            <a href={`${API}/orders/${sessionId}/gift-receipt`} target="_blank" rel="noopener noreferrer" data-testid="gift-receipt-download"
              className="inline-flex items-center gap-2 mt-5 text-[#8E9A86] hover:text-[#7A8772] font-medium">
              <Gift className="h-4 w-4" /> Print your gift receipt (no prices shown)
            </a>
          )}
          <Link to="/shop" data-testid="success-continue" className="inline-block mt-8 bg-[#8E9A86] text-white rounded-full px-8 py-3.5 font-medium hover:bg-[#7A8772] transition-colors">Continue shopping</Link>
        </div>
      )}
      {(state === "failed" || state === "timeout") && (
        <div data-testid="checkout-failed">
          <XCircle className="h-14 w-14 text-[#C4A4A4] mx-auto" />
          <h1 className="font-heading text-4xl text-[#3A3632] mt-6">{state === "timeout" ? "Still processing…" : "Payment not completed"}</h1>
          <p className="text-[#6B655F] mt-3">{state === "timeout" ? "If you completed payment, you'll receive a confirmation shortly." : "No worries — your cart is saved. Please try again."}</p>
          <Link to="/cart" className="inline-block mt-8 border border-[#8A8175] text-[#3A3632] rounded-full px-8 py-3.5 font-medium hover:bg-white transition-colors">Return to cart</Link>
        </div>
      )}
    </div>
  );
}
